import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowRight, Crown, Sailboat, Star } from 'lucide-react';
import { Badge } from '@/components/shared/Badge';
import { GlassCard } from '@/components/shared/GlassCard';
import { SectionHeader } from '@/components/shared/SectionHeader';
import { Button } from '@/components/ui/button';
import { brand } from '@/lib/theme/tokens';
import { photo, unsplash } from '@/lib/utils/img';
import { fadeUp, inViewOnce, staggerContainer } from '@/lib/motion/variants';
import { formatRupees } from '@/lib/utils/format';
import { useListings } from '@/features/marketplace/hooks/useListings';
import type { Listing } from '@/features/marketplace/types';

function imgSrc(listing: Listing, w: number): string {
  return listing.media_url ?? unsplash(photo.boat, { w });
}

export function HouseboatsGrid() {
  const { data: houseboats = [], isLoading } = useListings({ type: 'houseboat', per_page: 4 });

  const featured = houseboats[0] ?? null;
  const secondary = houseboats.slice(1, 4);

  return (
    <section className="mx-auto mt-24 max-w-7xl px-4 md:px-6">
      <SectionHeader
        kicker="Dal Lake exclusive · 38 boats"
        kickerColor={brand.saffron}
        title={
          <>
            Sleep on the water, <em>in a 100-year-old palace.</em>
          </>
        }
        actions={
          <Button asChild variant="outline" size="sm">
            <Link to="/houseboats">
              All houseboats <ArrowRight size={14} />
            </Link>
          </Button>
        }
      />

      {!isLoading && houseboats.length === 0 ? (
        <div className="mt-8 flex h-40 items-center justify-center text-sm text-ink-mute">
          No houseboats found
        </div>
      ) : (
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={inViewOnce}
          variants={staggerContainer}
          className="mt-8 grid grid-cols-1 gap-3.5 lg:grid-cols-[1.4fr_1fr] lg:gap-4"
        >
          {/* Featured · large card */}
          <motion.div variants={fadeUp}>
            {isLoading || !featured ? (
              <GlassCard className="relative overflow-hidden p-0 lg:h-full">
                <div className="aspect-[16/11] animate-pulse bg-white/10 lg:aspect-auto lg:h-full" />
              </GlassCard>
            ) : (
              <Link to={`/houseboats/${featured.slug}`} className="block h-full">
                <GlassCard interactive className="relative overflow-hidden p-0 lg:h-full">
                  <div className="relative aspect-[16/11] overflow-hidden lg:aspect-auto lg:h-full">
                    <img
                      src={imgSrc(featured, 1400)}
                      alt={featured.name}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 hover:scale-[1.04]"
                    />
                    <div
                      className="absolute inset-0"
                      style={{
                        background:
                          'linear-gradient(180deg, transparent 35%, rgba(0,0,0,0.4) 65%, rgba(0,0,0,0.9))',
                      }}
                    />

                    <div className="absolute left-5 top-5 flex flex-wrap gap-2">
                      <Badge color={brand.saffron} icon={Crown}>
                        Heritage feature
                      </Badge>
                    </div>

                    <div className="absolute inset-x-5 bottom-5 text-white">
                      <div
                        className="text-[11px] font-bold uppercase tracking-[0.2em]"
                        style={{ color: '#FFD79B' }}
                      >
                        Featured
                      </div>
                      <h3
                        className="font-display mt-2 text-3xl font-semibold leading-tight tracking-tight md:text-4xl"
                        style={{ textShadow: '0 2px 12px rgba(0,0,0,0.5)' }}
                      >
                        {featured.name}
                      </h3>
                      <div className="mt-2 inline-flex items-center gap-3 text-[13px] text-white/85">
                        {featured.location?.city && (
                          <span className="inline-flex items-center gap-1">
                            <Sailboat size={13} /> {featured.location.city}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1">
                          <Star size={13} fill={brand.warn} color={brand.warn} />
                          {featured.rating}
                        </span>
                      </div>
                      <div className="mt-4 flex items-end justify-between">
                        <div>
                          <span className="font-display text-3xl font-bold">
                            {formatRupees(Math.round((featured.price_paisa ?? 0) / 100))}
                          </span>
                          <span className="text-xs text-white/70"> /night</span>
                        </div>
                        <span
                          className="rounded-full px-3 py-1.5 text-xs font-semibold"
                          style={{
                            background: 'rgba(255,255,255,0.16)',
                            backdropFilter: 'blur(10px)',
                          }}
                        >
                          +{Math.round((featured.price_paisa ?? 0) / 100)} pts
                        </span>
                      </div>
                    </div>
                  </div>
                </GlassCard>
              </Link>
            )}
          </motion.div>

          {/* Secondary · 3 stacked */}
          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-3 lg:grid-cols-1 lg:gap-4">
            {isLoading
              ? Array.from({ length: 3 }).map((_, i) => (
                  <motion.div key={i} variants={fadeUp}>
                    <GlassCard className="grid h-full grid-cols-[120px_1fr] gap-0 overflow-hidden p-0">
                      <div className="aspect-square animate-pulse bg-white/10" />
                      <div className="space-y-2 p-3.5">
                        <div className="h-4 w-3/4 animate-pulse rounded bg-white/10" />
                        <div className="h-3 w-1/2 animate-pulse rounded bg-white/10" />
                        <div className="h-5 w-1/3 animate-pulse rounded bg-white/10" />
                      </div>
                    </GlassCard>
                  </motion.div>
                ))
              : secondary.map((h) => (
                  <motion.div key={h.id} variants={fadeUp}>
                    <Link to={`/houseboats/${h.slug}`} className="block h-full">
                      <GlassCard
                        interactive
                        className="grid h-full grid-cols-[120px_1fr] gap-0 overflow-hidden p-0"
                      >
                        <div className="relative aspect-square overflow-hidden">
                          <img
                            src={imgSrc(h, 400)}
                            alt={h.name}
                            loading="lazy"
                            decoding="async"
                            className="absolute inset-0 h-full w-full object-cover"
                          />
                        </div>
                        <div className="flex flex-col justify-between p-3.5">
                          <div>
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <div className="font-display mt-0.5 text-[15px] font-semibold leading-tight tracking-tight text-ink">
                                  {h.name}
                                </div>
                              </div>
                              <span className="inline-flex shrink-0 items-center gap-0.5 text-[11px] font-semibold text-ink">
                                <Star size={10} fill={brand.warn} color={brand.warn} />
                                {h.rating}
                              </span>
                            </div>
                            <div className="mt-1 text-[11px] text-ink-dim">
                              {h.location?.city}
                            </div>
                          </div>
                          <div className="mt-2 flex items-baseline justify-between">
                            <span>
                              <span className="font-display text-base font-bold text-ink">
                                {formatRupees(Math.round((h.price_paisa ?? 0) / 100))}
                              </span>
                              <span className="text-[11px] text-ink-mute"> /night</span>
                            </span>
                            <span
                              className="text-[10px] font-bold"
                              style={{ color: brand.teal }}
                            >
                              +{Math.round((h.price_paisa ?? 0) / 100)}
                            </span>
                          </div>
                        </div>
                      </GlassCard>
                    </Link>
                  </motion.div>
                ))}
          </div>
        </motion.div>
      )}
    </section>
  );
}
